import { SHARED_JOURNEY_UI_FALLBACKS } from './journeyUiText.js';
import { professionalTranslationFor } from '../data/professionalTranslationRuntime.js';
import { journeyInfoSupplementResources } from '../data/journeyInfoSupplementTranslations.js';
import { JOURNEY_SHARED_UI_TRANSLATIONS } from '../data/journeyNarrativeTranslations/sharedUi.js';
import { GENERIC_JOURNEY_NARRATIVE_TRANSLATIONS } from '../data/genericJourneyNarrativeTranslations/translations.js';
import { normalizeGenericJourneyNarrative } from '../data/genericJourneyNarrativeTranslations/index.js';
import { JOURNEY_PHRASE_TRANSLATIONS } from '../data/journeyPhraseTranslations/index.js';
import { DOCTOR_TITLE_PHRASE_TRANSLATIONS } from '../data/doctorTitlePhraseTranslations.js';
import patientJourneyGapResources from '../data/patientJourneyTranslationGapResources.js';
import { sectionNamingResources } from '../i18n/sectionNaming.js';
import { uniformColourTranslationCorrections } from '../data/uniformColourTranslationCorrections.js';
import { accessibilityDraftFor } from '../data/accessibilityDraftTranslations.js';
import { patientUiGapDraftFor } from '../data/patientUiGapDraftTranslations.js';
import { preAssessmentLegacyRouteText } from '../data/preAssessmentNurseLegacyRouteTranslations.js';
import { journeyPresentationDraftFor } from '../data/journeyPresentationDraftTranslations.js';
import { journeyDiscoveryDraftFor } from '../data/journeyDiscoveryDraftTranslations.js';
import { preparationQuestionMachineDraftFor } from '../data/preparationQuestionMachineDraftTranslations.js';

function pathValue(value, path) {
  return path.split('.').reduce((current, segment) => current?.[segment], value);
}

// Match the i18n 'loaded' handlers in src/i18n/index.js. All overrides
// originate from the SAME runtime resources consumed by the patient app.
// This deliberately reports the provenance, rather than quietly pretending
// the raw locale JSON is always what patients see.
export function effectiveTranslation(locale, namespace, path, storedText = '') {
  const lang = String(locale || '').toLowerCase().split('-')[0];
  let value = storedText || '';
  let provenance = value ? 'locale JSON' : 'missing';
  const apply = (candidate, source, force = false) => {
    if (typeof candidate === 'string' && candidate.trim() && (force || !value)) {
      value = candidate;
      provenance = source;
    }
  };
  if (namespace === 'whatYouMightHear' && path.startsWith('phrases.')) {
    apply(pathValue({ phrases: { ...JOURNEY_PHRASE_TRANSLATIONS[lang],
      ...DOCTOR_TITLE_PHRASE_TRANSLATIONS[lang] } }, path), 'runtime phrase supplement');
  }
  if (namespace === 'hospitalJourney') {
    apply(pathValue(JOURNEY_SHARED_UI_TRANSLATIONS[lang], path), 'runtime shared Journey overlay', true);
    const pack = GENERIC_JOURNEY_NARRATIVE_TRANSLATIONS[lang];
    const match = /^journeys\.generic-([^.]+)\.(.+)$/.exec(path);
    if (match && pack?.templates?.[match[1]]) {
      const normalized = normalizeGenericJourneyNarrative(match[1], pack.templates[match[1]], pack.commonLinks);
      apply(pathValue(normalized, match[2]), 'runtime generic Journey narrative', true);
    }
    apply(pathValue(patientJourneyGapResources[lang]?.hospitalJourney, path), 'runtime Journey gap correction', true);
    apply(SHARED_JOURNEY_UI_FALLBACKS[lang]?.[path], 'runtime Journey UI fallback');
  }
  if (namespace === 'roles' || namespace === 'common') {
    apply(pathValue(patientJourneyGapResources[lang]?.[namespace], path), 'runtime clinical copy correction', true);
  }
  if (namespace === 'common') {
    apply(uniformColourTranslationCorrections[lang]?.[path], 'runtime uniform colour correction', true);
  }
  if (namespace === 'journeyInfo') {
    apply(pathValue(journeyInfoSupplementResources(lang), path), 'runtime legacy JourneyInfo language pack', true);
    apply(journeyDiscoveryDraftFor(lang, path), 'runtime Journey discovery machine draft');
  }
  if (namespace === 'home' || namespace === 'nav' || namespace === 'journeyInfo') {
    apply(pathValue(sectionNamingResources(lang)[namespace], path), 'runtime navigation naming', true);
  }
  if (namespace === 'hospitalJourney') {
    apply(journeyPresentationDraftFor(lang, path), 'runtime Journey presentation machine draft');
    if (path.startsWith('ui.preparationQuestions.'))
      apply(preparationQuestionMachineDraftFor(lang, path.slice('ui.preparationQuestions.'.length)),
        'QA-checked machine translation (not professional)');
  }
  apply(accessibilityDraftFor(lang, namespace, path), 'runtime accessibility machine draft');
  apply(patientUiGapDraftFor(lang, namespace, path), 'runtime patient UI gap machine draft');
  if (namespace === 'roles')
    apply(preAssessmentLegacyRouteText(lang, path), 'existing pre-assessment role editorial draft');
  apply(professionalTranslationFor(lang, namespace, path), 'professionally approved correction', true);
  return { value, provenance, override: provenance !== 'locale JSON' && provenance !== 'missing' };
}
